/**
 * Pagination control matching the legacy `renderPag()` from index.html.
 * Same windowed page list (1..7 fully shown, otherwise … with first/last
 * always visible and the current page surrounded by neighbors).
 */

interface PaginationProps {
  total: number
  page: number
  pageSize: number
  onPageChange: (next: number) => void
}

export function Pagination({ total, page, pageSize, onPageChange }: PaginationProps) {
  const pages = Math.ceil(total / pageSize)
  if (pages <= 1) return null

  const start = (page - 1) * pageSize + 1
  const end = Math.min(page * pageSize, total)

  const items: Array<number | 'ellipsis-left' | 'ellipsis-right'> =
    pages <= 7
      ? Array.from({ length: pages }, (_, i) => i + 1)
      : page <= 4
        ? [1, 2, 3, 4, 5, 'ellipsis-right', pages]
        : page >= pages - 3
          ? [1, 'ellipsis-left', pages - 4, pages - 3, pages - 2, pages - 1, pages]
          : [1, 'ellipsis-left', page - 1, page, page + 1, 'ellipsis-right', pages]

  return (
    <div className="pagination" style={{ display: 'flex' }}>
      <div className="page-info">
        {start}–{end} of {total.toLocaleString()}
      </div>
      <div className="page-btns">
        <button className="page-btn" disabled={page === 1} onClick={() => onPageChange(page - 1)}>
          ‹
        </button>
        {items.map((item, i) =>
          typeof item === 'number' ? (
            <button
              key={i}
              className={`page-btn ${item === page ? 'current' : ''}`}
              onClick={() => onPageChange(item)}
            >
              {item}
            </button>
          ) : (
            <span key={i} className="page-btn" style={{ cursor: 'default' }}>
              …
            </span>
          ),
        )}
        <button
          className="page-btn"
          disabled={page === pages}
          onClick={() => onPageChange(page + 1)}
        >
          ›
        </button>
      </div>
    </div>
  )
}
